import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Smartphone, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function InstallPWA() {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Don't show on admin routes - admin has its own PWA component
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Skip for admin routes
    if (isAdminRoute) return;
    
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    if (isStandalone) {
      return;
    }

    // Check if iOS device
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Check if Safari browser on iOS (PWA can only be installed via Safari on iOS)
    const isSafariBrowser = /Safari/.test(navigator.userAgent) && 
                            !/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent);
    setIsSafari(isSafariBrowser);

    // Listen for beforeinstallprompt (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show banner immediately
      setShowInstallBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS Safari, show instructions
    if (isIOSDevice) {
      const dismissed = localStorage.getItem('pwa-ios-dismissed');
      const dismissedTime = dismissed ? new Date(dismissed).getTime() : 0;
      const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
      
      if (dismissedTime < oneDayAgo) {
        // Delay showing for better UX
        setTimeout(() => setShowInstallBanner(true), 2000);
      }
    }

    // For browsers that don't support beforeinstallprompt
    const timer = setTimeout(() => {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !deferredPrompt && !isIOSDevice) {
        setShowInstallBanner(true);
      }
    }, 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, [isAdminRoute]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      if (isIOS) {
        setShowIOSInstructions(true);
      }
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
    if (isIOS) {
      localStorage.setItem('pwa-ios-dismissed', new Date().toISOString());
    }
  };

  // Don't render for admin routes - admin has its own PWA install component
  if (isAdminRoute || !showInstallBanner) return null;

  return (
    <AnimatePresence>
      {showInstallBanner && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
            {showIOSInstructions || (isIOS && !deferredPrompt) ? (
              // iOS Instructions - Enhanced
              <div className="p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">Install di iPhone</h3>
                      <p className="text-xs text-slate-500">Produk Digital</p>
                    </div>
                  </div>
                  <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600 p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                {!isSafari && isIOS ? (
                  // Warning for non-Safari browsers on iOS
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                    <p className="text-amber-800 text-sm">
                      <strong>Catatan:</strong> Install aplikasi di iPhone hanya bisa melalui <strong>Safari</strong>, bukan Chrome atau browser lain.
                    </p>
                    <p className="text-amber-700 text-xs mt-2">
                      Buka aplikasi ini di Safari untuk install.
                    </p>
                  </div>
                ) : null}
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sky-600 font-bold text-xs">1</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-slate-700">
                        <span>Tekan tombol</span>
                        <Share className="w-4 h-4 text-sky-500" />
                        <span className="font-medium">Share</span>
                      </div>
                      <p className="text-xs text-slate-500">Di bagian bawah Safari</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sky-600 font-bold text-xs">2</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-slate-700">
                        <span>Pilih</span>
                        <PlusSquare className="w-4 h-4 text-sky-500" />
                        <span className="font-medium">"Add to Home Screen"</span>
                      </div>
                      <p className="text-xs text-slate-500">Scroll ke bawah jika tidak terlihat</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sky-600 font-bold text-xs">3</span>
                    </div>
                    <div className="flex-1 text-slate-700">
                      Tekan <span className="font-medium text-sky-600">"Add"</span> di pojok kanan atas
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={handleDismiss}
                  variant="outline"
                  size="sm"
                  className="w-full mt-4 rounded-xl"
                >
                  Mengerti
                </Button>
              </div>
            ) : (
              // Install Banner (Android/Desktop)
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800 mb-1">Install Produk Digital</h3>
                    <p className="text-sm text-slate-500 mb-3">
                      Akses lebih cepat langsung dari home screen HP Anda
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleInstall}
                        size="sm"
                        className="btn-flow rounded-full"
                      >
                        Install
                      </Button>
                      <Button 
                        onClick={handleDismiss}
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                      >
                        Nanti
                      </Button>
                    </div>
                  </div>
                  <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
